import {
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react";

export function useLoadingMessageQueue() {
  const [queue, setQueue] = useState<string[]>([]);
  const [current, setCurrent] = useState<string | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const enqueue = useCallback((msg: string) => {
    setQueue((q) => [...q, msg]);
  }, []);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setQueue([]);
    setCurrent(null);
  }, []);

  useEffect(() => {
    if (!current && queue.length > 0) {
      setCurrent(queue[0]);
      timerRef.current = setTimeout(() => {
        setQueue((q) => q.slice(1));
        setCurrent(null);
      }, 1500);
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [queue, current]);

  return { enqueue, clear, currentLoadingMessage: current };
}

export function LoadingMessageQueue({
  loadingMessage,
  children,
}: {
  loadingMessage?: string | null;
  children: (msg: string | null) => ReactNode;
}) {
  const { enqueue, clear, currentLoadingMessage } = useLoadingMessageQueue();

  useEffect(() => {
    if (loadingMessage) {
      enqueue(loadingMessage);
    } else {
      clear();
    }
  }, [loadingMessage, enqueue, clear]);

  return <>{children(currentLoadingMessage)}</>;
}
